/**
 * Payment link hooks — Slice B of #27.
 *
 * usePaymentLinks(invoiceId)   — GET /api/invoices/:id/payment-links
 * useCreatePaymentLink()       — POST /api/invoices/:id/payment-links
 * useRevokePaymentLink()       — DELETE /api/payment-links/:id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { PaymentLinkListItem, PaymentLinkCreateResponse } from '@aelita/shared';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const paymentLinkKeys = {
  list: (invoiceId: string) => ['payment-links', invoiceId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function usePaymentLinks(invoiceId: string | undefined) {
  return useQuery({
    queryKey: paymentLinkKeys.list(invoiceId ?? ''),
    queryFn: async () => {
      const response = await api.get<PaymentLinkListItem[]>(`/invoices/${invoiceId}/payment-links`);
      return response.data ?? [];
    },
    enabled: !!invoiceId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreatePaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      expiresInDays,
    }: {
      invoiceId: string;
      expiresInDays?: number;
    }) => {
      const response = await api.post<PaymentLinkCreateResponse>(
        `/invoices/${invoiceId}/payment-links`,
        expiresInDays !== undefined ? { expiresInDays } : {},
      );
      return response.data as PaymentLinkCreateResponse;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentLinkKeys.list(variables.invoiceId) });
    },
  });
}

export function useRevokePaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ linkId, invoiceId }: { linkId: string; invoiceId: string }) => {
      const response = await api.delete<PaymentLinkListItem>(`/payment-links/${linkId}`);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentLinkKeys.list(variables.invoiceId) });
    },
  });
}
